/**
 * MasterDlcList — Admin drag-and-drop reorder of a master game's DLCs, with a
 * per-DLC "Order Achievements" nested dialog for reordering the achievements
 * inside that DLC. Persistence happens via two dedicated endpoints:
 *   POST /api/master/games/{gameId}/dlcs/reorder
 *   POST /api/master/games/{gameId}/achievements/reorder
 *
 * DLCs are always displayed sorted by `sortOrder` when set (falls back to
 * releaseDate then alphabetical). This is the single source of truth for
 * every user viewing the game info page.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "@/lib/api";
import { toast } from "sonner";
import {
  GripVertical,
  Image as ImageIcon,
  Edit2,
  Trash2,
  ListOrdered,
  Save,
  X,
  Loader2,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

// Return DLCs in canonical display order (sortOrder → releaseDate → name).
function sortDlcsForDisplay(dlcs) {
  const arr = [...(dlcs || [])];
  arr.sort((a, b) => {
    const aHas = a.sortOrder != null;
    const bHas = b.sortOrder != null;
    if (aHas && bHas) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (aHas) return -1;
    if (bHas) return 1;
    const aDate = a.releaseDate || "";
    const bDate = b.releaseDate || "";
    if (aDate && bDate) return aDate.localeCompare(bDate);
    if (aDate) return -1;
    if (bDate) return 1;
    return (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
  });
  return arr;
}

function SortableDlcRow({ dlc, dlcAchievements, onEdit, onDelete, onCoverSearch, onOrderAchievements }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dlc.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const achCount = dlcAchievements.length;
  const totalScore = dlcAchievements.reduce((sum, a) => sum + (a.score || 0), 0);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-wrap items-center gap-2 p-2 rounded bg-white/5"
      data-testid={`dlc-row-${dlc.id}`}
    >
      <div className="flex items-center gap-2 grow min-w-[200px]">
      <button
        {...attributes}
        {...listeners}
        style={{ touchAction: "none" }}
        className="p-2 -m-1 text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing flex-shrink-0"
        title="Drag to reorder"
        data-testid={`dlc-drag-${dlc.id}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      {dlc.boxArtUrl ? (
        <img
          src={dlc.boxArtUrl}
          alt={dlc.name}
          className="w-10 h-10 rounded object-cover flex-shrink-0"
          onError={(e) => (e.target.style.display = "none")}
        />
      ) : (
        <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
          <ImageIcon className="w-4 h-4 text-white/30" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white break-words line-clamp-2" title={dlc.name}>{dlc.name}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {dlc.region && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 flex-shrink-0">
              {dlc.region}
            </span>
          )}
          {dlc.sortOrder != null && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 flex-shrink-0"
              title="Manual sort order"
            >
              #{dlc.sortOrder + 1}
            </span>
          )}
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
            {achCount} achievement{achCount !== 1 ? "s" : ""}
          </span>
          {totalScore > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
              {totalScore}G
            </span>
          )}
          {dlc.releaseDate && <span className="text-[10px] text-white/40">{dlc.releaseDate}</span>}
        </div>
      </div>
      </div>
      <div className="flex gap-1 flex-shrink-0 ml-auto">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onOrderAchievements(dlc)}
          className="text-cyan-400/70 hover:text-cyan-300 h-7 w-7 p-0"
          title="Order achievements in this DLC"
          data-testid={`dlc-order-achievements-${dlc.id}`}
        >
          <ListOrdered className="w-3 h-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onCoverSearch(dlc)}
          className="text-blue-400/60 hover:text-blue-400 h-7 w-7 p-0"
          title="Search for DLC cover art"
        >
          <ImageIcon className="w-3 h-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onEdit(dlc)}
          className="text-white/50 hover:text-white h-7 w-7 p-0"
        >
          <Edit2 className="w-3 h-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(dlc.id)}
          className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

export function MasterDlcList({
  masterGameId,
  dlcs,
  selectedGameAchievements,
  onEdit,
  onDelete,
  onCoverSearch,
  onReorderPersisted,
}) {
  const sortedDlcs = useMemo(() => sortDlcsForDisplay(dlcs), [dlcs]);
  const [items, setItems] = useState(sortedDlcs);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderAchDlc, setOrderAchDlc] = useState(null);

  // Whenever the incoming dlcs prop changes (edits, adds, deletes), resync
  useEffect(() => {
    setItems(sortDlcsForDisplay(dlcs));
  }, [dlcs]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Long-press activates drag on touch devices so scrolling still works when
    // you swipe over the list. 200ms feels natural without being frustrating.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((d) => d.id === active.id);
    const newIdx = items.findIndex((d) => d.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(items, oldIdx, newIdx);
    // Optimistic update
    setItems(next);
    setSavingOrder(true);
    try {
      await axios.post(`${API}/master/games/${masterGameId}/dlcs/reorder`, {
        orderedIds: next.map((d) => d.id),
      });
      onReorderPersisted && onReorderPersisted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save DLC order");
      setItems(sortDlcsForDisplay(dlcs));
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] text-white/40">
          Drag to reorder (long-press on mobile) — this is what everyone sees on the game page.
        </p>
        {savingOrder && (
          <span className="flex items-center gap-1 text-[11px] text-cyan-400/70">
            <Loader2 className="w-3 h-3 animate-spin" /> Saving order…
          </span>
        )}
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((d) => d.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((dlc) => {
              const dlcAchs = selectedGameAchievements.filter(
                (a) => a.dlcId === dlc.id || a.dlcName === dlc.name
              );
              return (
                <SortableDlcRow
                  key={dlc.id}
                  dlc={dlc}
                  dlcAchievements={dlcAchs}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onCoverSearch={onCoverSearch}
                  onOrderAchievements={setOrderAchDlc}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      <OrderAchievementsDialog
        open={!!orderAchDlc}
        onOpenChange={(v) => !v && setOrderAchDlc(null)}
        masterGameId={masterGameId}
        dlc={orderAchDlc}
        achievements={
          orderAchDlc
            ? selectedGameAchievements.filter(
                (a) => a.dlcId === orderAchDlc.id || a.dlcName === orderAchDlc.name
              )
            : []
        }
        onSaved={() => onReorderPersisted && onReorderPersisted()}
      />
    </>
  );
}

// ============ Achievement drag-reorder within one DLC ============

function SortableAchievementRow({ ach }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ach.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded"
      data-testid={`ach-row-${ach.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        style={{ touchAction: "none" }}
        className="p-2 -m-1 text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      {ach.imageUrl && (
        <img
          src={ach.imageUrl}
          alt=""
          className="w-8 h-8 rounded object-cover flex-shrink-0"
          onError={(e) => (e.target.style.display = "none")}
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{ach.name}</p>
        {ach.score > 0 && <p className="text-[10px] text-yellow-400/70">{ach.score}G</p>}
      </div>
    </div>
  );
}

function OrderAchievementsDialog({ open, onOpenChange, masterGameId, dlc, achievements, onSaved }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (open && achievements) {
      // Sort by existing sortOrder then name so users see the current state
      const sorted = [...achievements].sort((a, b) => {
        const so = (a.sortOrder ?? 10 ** 9) - (b.sortOrder ?? 10 ** 9);
        if (so !== 0) return so;
        return (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
      });
      setItems(sorted);
      setDirty(false);
    }
  }, [open, achievements]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((a) => a.id === active.id);
    const newIdx = items.findIndex((a) => a.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    setItems((prev) => arrayMove(prev, oldIdx, newIdx));
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/master/games/${masterGameId}/achievements/reorder`, {
        orderedIds: items.map((a) => a.id),
        dlcId: dlc?.id || null,
      });
      toast.success(`Saved order for ${items.length} achievements`);
      setDirty(false);
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save achievement order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[85vh] flex flex-col"
        data-testid="order-achievements-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListOrdered className="w-5 h-5 text-cyan-400" />
            Order Achievements · {dlc?.name || "DLC"}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Drag to change the display order. This is what every user will see
            on the game info page when the default sort is active.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-2 py-2">
          {items.length === 0 ? (
            <p className="text-sm text-white/40 text-center py-6">
              No achievements linked to this DLC yet.
            </p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                {items.map((a) => (
                  <SortableAchievementRow key={a.id} ach={a} />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/10">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/70">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !dirty || items.length === 0}
            className="bg-cyan-600 hover:bg-cyan-700"
            data-testid="save-achievement-order-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save order
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MasterDlcList;
